// @flow

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { rgba } from "polished";
import useClientDimensions from "src/hooks/useClientDimensions";

import Archived from "./Archived";
import { Value, DueStatus, StatusTextContainer } from "./styles";
import * as colors from "src/styles/constants/colors";
import { ACTIVE } from "src/constants/status";
import Icon from "src/icons";
import {
  getStatusTitle,
  getTemplateId,
  getActiveStatus,
  isStatusDisabled,
  getStatusArchiveMode,
  getChatroomManualArchived,
} from "src/reducers";
import type { RoomId } from "src/types";

type Props = {
  isOverDue?: boolean,
  id: number,
  hideArchive?: boolean,
  location?: string,
  roomId?: RoomId,
};

const Custom = ({ isOverDue, hideArchive, location, id, roomId }: Props) => {
  const { vw } = useClientDimensions();
  const templateId = useSelector(({ app }) =>
    roomId ? getTemplateId(app, `${roomId}`) : null,
  );
  const title = useSelector(({ app }) => getStatusTitle(app, `${id}`));
  const active = useSelector(({ app }) =>
    templateId ? getActiveStatus(app, `${templateId}`, `${id}`) : null,
  );
  const archiveMode = useSelector(({ app }) =>
    templateId ? getStatusArchiveMode(app, `${templateId}`, `${id}`) : null,
  );
  const manuallyArchived = useSelector(({ app }) =>
    roomId ? getChatroomManualArchived(app, roomId) : null,
  );
  const disabled = useSelector(({ app }) =>
    roomId ? isStatusDisabled(app, roomId) : null,
  );

  const color = useMemo(
    () =>
      // If workflow had been converted to feed apply this color
      // i.e if disabled is true
      disabled
        ? rgba(colors.secondary, 0.2)
        : active
          ? rgba(colors.secondary, 0.2)
          : rgba(colors.success, 0.2),
    [active, disabled],
  );

  const textColor = useMemo(
    () =>
      disabled ? colors.primary : active ? colors.primary : colors.success,
    [active, disabled],
  );

  const archived =
    manuallyArchived == null ? archiveMode === "archive" : manuallyArchived;

  if (isOverDue && !archived && active) {
    return (
      <Value
        color={color}
        textColor={textColor}
        location={location}
        overdue={isOverDue}
      >
        <DueStatus location={location}>
          <div>{title}</div>
        </DueStatus>
        <Icon type="alarmClock" />
      </Value>
    );
  }

  return (
    <Value color={color} textColor={textColor} location={location} vw={vw}>
      <StatusTextContainer>{disabled ? ACTIVE : title}</StatusTextContainer>
      {archived && !hideArchive ? <Archived /> : null}
    </Value>
  );
};

export default Custom;
